/**
 * The Environment card's draft: pure functions over the saved override set,
 * the manifest's requirements, and the environment on disk.
 *
 * A draft is an `OverrideEntry[]` the card edits locally. Nothing here talks
 * to the API; the card sends the whole draft once on Apply.
 */
import type { EnvironmentEntry, OverrideEntry } from '@optum/api-client/types';

export type RowOrigin = 'plugin' | 'declared' | 'added' | 'changed' | 'removed';

/** One package row of the card, derived from the environment and the draft. */
export interface EnvironmentRow {
  name: string;
  /** The version on disk; null for a draft addition not yet built. */
  version: string | null;
  optum: boolean;
  origin: RowOrigin;
  /** The manifest's specifier for a declared name (`>=2.31`), empty string for none, null when undeclared. */
  declared: string | null;
  /** The draft entry for this name, if any. */
  entry: OverrideEntry | null;
  /** Whether the draft differs from the saved set for this name. */
  dirty: boolean;
}

/** What an Apply would change, relative to the saved set. */
export interface DraftSummary {
  added: OverrideEntry[];
  changed: OverrideEntry[];
  removed: OverrideEntry[];
  /** Saved entries the draft drops, so the manifest's version returns. */
  dropped: OverrideEntry[];
  /** Saved entries the draft keeps unchanged. */
  kept: OverrideEntry[];
}

const OPTUM_PREFIX = 'optum-';
const NAME_END = /[\s<>=!~;([]/;
const NORMALIZE = /[-_.]+/g;
// A specifier starts with an operator; anything else typed in the field is a
// version on its own and means exactly that version.
const OPERATOR_START = /^[<>=!~]/;

/** `1.1.0` becomes `==1.1.0`; a string that already carries an operator is kept. */
export function normalizeSpecifier(raw: string | null | undefined): string | null {
  const trimmed = raw?.replace(/\s+/g, '') ?? '';
  if (trimmed.length === 0) return null;
  return OPERATOR_START.test(trimmed) ? trimmed : `==${trimmed}`;
}

/** PEP 503 canonical distribution name. */
export function canonicalName(raw: string): string {
  return raw.trim().toLowerCase().replace(NORMALIZE, '-').replace(/^-|-$/g, '');
}

/** The distribution name of a PEP 508 requirement line. */
export function requirementName(requirement: string): string {
  const trimmed = requirement.trim();
  const end = trimmed.search(NAME_END);
  return canonicalName(end === -1 ? trimmed : trimmed.slice(0, end));
}

/** The specifier of a PEP 508 line (`>=2.31`), without extras or markers; empty when none. */
export function requirementSpecifier(requirement: string): string {
  const noMarker = requirement.split(';')[0] ?? '';
  // Extras and the legacy parenthesized form are both PEP 508; neither is a specifier.
  const noExtras = noMarker.replace(/\[[^\]]*\]/, '').replace(/[()]/g, '');
  const match = /[<>=!~].*$/.exec(noExtras);
  return match ? match[0].replace(/\s+/g, '') : '';
}

/** Canonical name to manifest specifier for every declared requirement. */
export function declaredSpecifiers(requires: string[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const line of requires) {
    const name = requirementName(line);
    if (name) out.set(name, requirementSpecifier(line));
  }
  return out;
}

function sameEntry(a: OverrideEntry | null, b: OverrideEntry | null): boolean {
  if (a === null || b === null) return a === b;
  return a.name === b.name && a.action === b.action && (a.specifier ?? null) === (b.specifier ?? null);
}

function entryFor(entries: OverrideEntry[], name: string): OverrideEntry | null {
  return entries.find((e) => e.name === name) ?? null;
}

function origin(
  name: string,
  pluginName: string,
  declared: Map<string, string>,
  entry: OverrideEntry | null,
): RowOrigin {
  if (name === canonicalName(pluginName)) return 'plugin';
  if (entry === null) return 'declared';
  if (entry.action === 'remove') return 'removed';
  // A require on a name the manifest declares changes its version; on any
  // other name it adds a requirement the manifest never made.
  return declared.has(name) ? 'changed' : 'added';
}

/** One row per package in the environment or the draft, plugin first, optum before public. */
export function buildRows(input: {
  pluginName: string;
  environment: EnvironmentEntry[];
  requires: string[];
  saved: OverrideEntry[];
  draft: OverrideEntry[];
}): EnvironmentRow[] {
  const declared = declaredSpecifiers(input.requires);
  const names = new Set<string>();
  for (const e of input.environment) names.add(e.name);
  for (const e of input.draft) names.add(e.name);
  for (const e of input.saved) names.add(e.name);

  const rows: EnvironmentRow[] = [];
  for (const name of names) {
    const onDisk = input.environment.find((e) => e.name === name) ?? null;
    const entry = entryFor(input.draft, name);
    const savedEntry = entryFor(input.saved, name);
    rows.push({
      name,
      version: onDisk?.version ?? null,
      optum: onDisk?.optum ?? name.startsWith(OPTUM_PREFIX),
      origin: origin(name, input.pluginName, declared, entry),
      declared: declared.get(name) ?? null,
      entry,
      dirty: !sameEntry(entry, savedEntry),
    });
  }
  const rank = (r: EnvironmentRow): number => (r.origin === 'plugin' ? 0 : r.optum ? 1 : 2);
  return rows.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

function upsert(draft: OverrideEntry[], entry: OverrideEntry): OverrideEntry[] {
  const rest = draft.filter((e) => e.name !== entry.name);
  return [...rest, entry];
}

/** Require `name`, optionally at `specifier`; replaces any entry for the name. */
export function addRequire(
  draft: OverrideEntry[],
  name: string,
  specifier: string | null,
): OverrideEntry[] {
  return upsert(draft, {
    name: canonicalName(name),
    action: 'require',
    specifier: normalizeSpecifier(specifier),
  });
}

/** Pin `name` to `specifier`; the same operation as a require with a specifier. */
export function setSpecifier(
  draft: OverrideEntry[],
  name: string,
  specifier: string,
): OverrideEntry[] {
  return addRequire(draft, name, specifier);
}

/** Take `name` out of the environment. */
export function removePackage(draft: OverrideEntry[], name: string): OverrideEntry[] {
  return upsert(draft, { name: canonicalName(name), action: 'remove', specifier: null });
}

/** Drop the entry for `name`, so the manifest decides it again. */
export function dropEntry(draft: OverrideEntry[], name: string): OverrideEntry[] {
  const canonical = canonicalName(name);
  return draft.filter((e) => e.name !== canonical);
}

/** Put the saved entry for `name` back, or drop the draft's if none is saved. */
export function undoChange(
  draft: OverrideEntry[],
  saved: OverrideEntry[],
  name: string,
): OverrideEntry[] {
  const savedEntry = entryFor(saved, canonicalName(name));
  const without = dropEntry(draft, name);
  return savedEntry ? [...without, savedEntry] : without;
}

function sorted(entries: OverrideEntry[]): OverrideEntry[] {
  return [...entries].sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether Apply would send anything other than the saved set, order ignored. */
export function isDirty(saved: OverrideEntry[], draft: OverrideEntry[]): boolean {
  const a = sorted(saved);
  const b = sorted(draft);
  if (a.length !== b.length) return true;
  return a.some((entry, i) => !sameEntry(entry, b[i] ?? null));
}

/** The changes an Apply makes, for the confirmation. */
export function summarizeDraft(
  saved: OverrideEntry[],
  draft: OverrideEntry[],
  requires: string[],
): DraftSummary {
  const declared = declaredSpecifiers(requires);
  const summary: DraftSummary = { added: [], changed: [], removed: [], dropped: [], kept: [] };
  for (const entry of sorted(draft)) {
    const savedEntry = entryFor(saved, entry.name);
    if (sameEntry(entry, savedEntry)) {
      summary.kept.push(entry);
      continue;
    }
    if (entry.action === 'remove') summary.removed.push(entry);
    else if (declared.has(entry.name)) summary.changed.push(entry);
    else summary.added.push(entry);
  }
  for (const entry of sorted(saved)) {
    if (entryFor(draft, entry.name) === null) summary.dropped.push(entry);
  }
  return summary;
}
